import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useSearchParams } from "react-router";
import { fetchDataManagementServiceMember, fetchDataManagementServiceMemberTimestamps } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import Modal from "../modal/Modal";
import ReactDiffViewer from "react-diff-viewer";
import Select from "react-select";

const PAGE_SIZE = 20;
const TIMESTAMP_PAGE_SIZE = 100;

const inputStyle = {
    fontSize: "1rem",
    minWidth: "250px",
    padding: "1rem",
    borderRadius: ".4rem",
    border: "1px solid var(--lighterGrey)",
    outline: "none",
    backgroundColor: "var(--white)"
};

const sortByKeysDeep = (value) => {
    if (Array.isArray(value)) return value.map(sortByKeysDeep);
    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((acc, key) => {
            acc[key] = sortByKeysDeep(value[key]);
            return acc;
        }, {});
    }
    return value;
};

const toDiffJson = (row) => {
    if (!row) return '';
    return JSON.stringify(sortByKeysDeep(row), null, 4);
};

const changedFieldNames = (before, after) => {
    const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])].sort();
    return keys.filter((key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]));
};

const compareSelectStyles = {
    option: (provided, state) => {
        if (state.isDisabled) {
            return {
                ...provided,
                color: 'var(--black)',
                backgroundColor: 'var(--lighterGrey)',
                opacity: 0.45,
                cursor: 'not-allowed',
            };
        }

        return {
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--skyDark)'
                : state.isFocused
                    ? 'var(--lighterBlue)'
                    : 'var(--white)',
            color: state.isSelected ? 'var(--white)' : 'var(--black)',
            ':active': {
                backgroundColor: 'var(--skyDark)',
                color: 'var(--white)',
            },
        };
    },
};

const snapshotSelectProps = {
    className: "react-select-container",
    classNamePrefix: "react-select",
    styles: compareSelectStyles,
    isSearchable: false,
    isClearable: false,
    menuPlacement: "auto",
    theme: (theme) => ({
        ...theme,
        colors: {
            ...theme.colors,
            primary: 'var(--skyDark)',
            primary25: 'var(--lighterBlue)',
            primary50: 'var(--lightestBlue)',
            primary75: 'var(--lightBlue)',
        },
    }),
};

const ServiceMember = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memberUuid = searchParams.get('memberUuid')?.trim() || '';
    const tableVersion = searchParams.get('tableVersion')?.trim() || '';

    const [memberUuidInput, setMemberUuidInput] = useState(memberUuid);
    const [tableVersionInput, setTableVersionInput] = useState(tableVersion);
    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [beforeTs, setBeforeTs] = useState('');
    const [afterTs, setAfterTs] = useState('');
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareError, setCompareError] = useState(null);
    const [compareBeforeRow, setCompareBeforeRow] = useState(null);
    const [compareAfterRow, setCompareAfterRow] = useState(null);
    const compareInitKey = useRef('');

    useEffect(() => {
        setMemberUuidInput(memberUuid);
        setTableVersionInput(tableVersion);
    }, [memberUuid, tableVersion]);

    const updateFilters = useCallback(
        debounce((uuid, version) => {
            const next = {};
            if (uuid?.trim()) next.memberUuid = uuid.trim();
            if (version?.trim()) next.tableVersion = version.trim();
            setSearchParams(next, { replace: true });
        }, 500),
        [setSearchParams]
    );

    const { ref, inView } = useInView();

    const {
        data,
        fetchNextPage,
        isLoading,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchOnWindowFocus: false,
        queryKey: ['athena-service-member', memberUuid, tableVersion],
        queryFn: ({ pageParam }) => fetchDataManagementServiceMember({
            memberUuid,
            ...(tableVersion ? { tableVersion } : {}),
            offset: pageParam || 0,
            limit: PAGE_SIZE
        }),
        select: (data) => ({
            pages: data.pages.flat(),
            pageParams: data.pageParams,
        }),
        getNextPageParam: (lastPage, allPages) => {
            if (!Array.isArray(lastPage) || lastPage.length < PAGE_SIZE) return undefined;
            return allPages.reduce((sum, page) => sum + (page?.length || 0), 0);
        },
        staleTime: 0,
        enabled: !!memberUuid
    });

    const {
        data: timestampData,
        fetchNextPage: fetchNextTimestampPage,
        hasNextPage: hasNextTimestampPage,
        isFetchingNextPage: isFetchingNextTimestampPage,
        isLoading: isTimestampsLoading
    } = useInfiniteQuery({
        retry: false,
        refetchOnWindowFocus: false,
        queryKey: ['athena-service-member-timestamps', memberUuid, tableVersion],
        queryFn: ({ pageParam }) => fetchDataManagementServiceMemberTimestamps({
            memberUuid,
            ...(tableVersion ? { tableVersion } : {}),
            offset: pageParam || 0,
            limit: TIMESTAMP_PAGE_SIZE
        }),
        getNextPageParam: (lastPage, allPages) => {
            if (!Array.isArray(lastPage) || lastPage.length < TIMESTAMP_PAGE_SIZE) return undefined;
            return allPages.reduce((sum, page) => sum + (page?.length || 0), 0);
        },
        staleTime: 0,
        enabled: !!memberUuid
    });

    useEffect(() => {
        if (hasNextTimestampPage && !isFetchingNextTimestampPage) {
            fetchNextTimestampPage();
        }
    }, [hasNextTimestampPage, isFetchingNextTimestampPage, fetchNextTimestampPage]);

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const items = useMemo(() => data?.pages?.flat() || [], [data]);

    const snapshotTimestamps = useMemo(
        () => [...new Set((timestampData?.pages || []).flat().filter(Boolean))],
        [timestampData]
    );
    const timestampsOldestFirst = useMemo(
        () => [...snapshotTimestamps].reverse(),
        [snapshotTimestamps]
    );
    const newestTs = snapshotTimestamps[0];

    const beforeOptions = useMemo(
        () => timestampsOldestFirst.map((ts) => ({
            value: ts,
            label: ts,
            isDisabled: ts === newestTs,
        })),
        [timestampsOldestFirst, newestTs]
    );

    const afterOptions = useMemo(
        () => timestampsOldestFirst.map((ts) => ({
            value: ts,
            label: ts,
            isDisabled: !beforeTs || ts <= beforeTs,
        })),
        [timestampsOldestFirst, beforeTs]
    );

    useEffect(() => {
        const key = `${memberUuid}|${tableVersion}`;
        if (compareInitKey.current !== key) {
            compareInitKey.current = key;
            setBeforeTs('');
            setAfterTs('');
            setIsCompareOpen(false);
            setCompareBeforeRow(null);
            setCompareAfterRow(null);
            setCompareError(null);
        }
        if (snapshotTimestamps.length >= 2 && !beforeTs && !afterTs) {
            setAfterTs(snapshotTimestamps[0]);
            setBeforeTs(snapshotTimestamps[1]);
        }
    }, [memberUuid, tableVersion, snapshotTimestamps, beforeTs, afterTs]);

    const setBeforeSnapshot = (ts) => {
        setBeforeTs(ts);
        if (!ts) {
            setAfterTs('');
            return;
        }
        if (!afterTs || afterTs <= ts) {
            const nextAfter = timestampsOldestFirst.find((value) => value > ts);
            setAfterTs(nextAfter || '');
        }
    };

    const canCompare = beforeTs && afterTs && afterTs > beforeTs;

    const openCompare = async () => {
        if (!canCompare) return;
        setIsCompareOpen(true);
        setCompareLoading(true);
        setCompareError(null);
        setCompareBeforeRow(null);
        setCompareAfterRow(null);

        const snapshotParams = (snapshotTimestamp) => ({
            memberUuid,
            ...(tableVersion ? { tableVersion } : {}),
            snapshotTimestamp,
            limit: 1
        });

        try {
            const [beforeItems, afterItems] = await Promise.all([
                fetchDataManagementServiceMember(snapshotParams(beforeTs)),
                fetchDataManagementServiceMember(snapshotParams(afterTs)),
            ]);
            setCompareBeforeRow(beforeItems?.[0] || null);
            setCompareAfterRow(afterItems?.[0] || null);
        } catch (err) {
            setCompareError(err.message);
        } finally {
            setCompareLoading(false);
        }
    };

    const changedFields = useMemo(
        () => changedFieldNames(compareBeforeRow, compareAfterRow),
        [compareBeforeRow, compareAfterRow]
    );

    const orderedDiff = useMemo(() => {
        if (!compareBeforeRow || !compareAfterRow) return null;
        return {
            oldValue: toDiffJson(compareBeforeRow),
            newValue: toDiffJson(compareAfterRow),
            leftTitle: `Before  ${beforeTs}`,
            rightTitle: `After  ${afterTs}`,
        };
    }, [compareBeforeRow, compareAfterRow, beforeTs, afterTs]);

    let columns = [
        'snapshot_timestamp', 'memberUuid', 'tableVersion', 'firstName', 'lastName', 'dob',
        'certificateNumber', 'systemIdentifier', 'clientId', 'planId', 'eligibilityStatus', 'created'
    ];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'defaultEmail', 'city', 'state', 'zipCode', 'eligibilityBenefitPlan',
            'eligibilityEffectiveDate', 'eligibilityTerminationDate', 'modified', 'gender'
        ];
    }

    return (
        <div className="container">
            <div>
                <p className="title">service_member</p>
                <p className="sub-title">workgroup: data-management | structuredzone.service_member | filter by memberUuid (required) and tableVersion (optional)</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem', padding: '1rem 1rem .5rem 0rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        style={inputStyle}
                        type="text"
                        placeholder="memberUuid"
                        autoComplete="off"
                        value={memberUuidInput}
                        onChange={(e) => {
                            setMemberUuidInput(e.target.value);
                            updateFilters(e.target.value, tableVersionInput);
                        }}
                    />
                    <input
                        style={inputStyle}
                        type="text"
                        placeholder="tableVersion (optional)"
                        autoComplete="off"
                        value={tableVersionInput}
                        onChange={(e) => {
                            setTableVersionInput(e.target.value);
                            updateFilters(memberUuidInput, e.target.value);
                        }}
                    />
                </div>

                {snapshotTimestamps.length >= 2 && (
                    <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
                        <div style={{ width: '250px' }}>
                            <Select
                                {...snapshotSelectProps}
                                options={beforeOptions}
                                value={beforeOptions.find((option) => option.value === beforeTs) || null}
                                onChange={(option) => setBeforeSnapshot(option?.value || '')}
                                placeholder="Before snapshot"
                            />
                        </div>
                        <span style={{ color: 'var(--grey)' }}>→</span>
                        <div style={{ width: '250px' }}>
                            <Select
                                {...snapshotSelectProps}
                                options={afterOptions}
                                value={afterOptions.find((option) => option.value === afterTs) || null}
                                onChange={(option) => setAfterTs(option?.value || '')}
                                placeholder="After snapshot"
                            />
                        </div>
                        <p
                            className="clickable-link"
                            style={{
                                color: canCompare ? 'var(--green)' : 'var(--grey)',
                                cursor: canCompare ? 'pointer' : 'default',
                                fontWeight: 600
                            }}
                            onClick={() => canCompare && openCompare()}
                        >
                            Compare JSON
                        </p>
                    </div>
                )}
            </div>

            {snapshotTimestamps.length >= 2 && (
                <p className="sub-title" style={{ textAlign: 'right', padding: '0 1rem .75rem 0' }}>
                    Pick two snapshot dates, then Compare JSON to see a before/after diff of the member record.
                    {(isTimestampsLoading || hasNextTimestampPage) ? ' Loading remaining snapshot dates…' : ` ${snapshotTimestamps.length} dates loaded.`}
                </p>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        updateFilters.cancel();
                        setMemberUuidInput('');
                        setTableVersionInput('');
                        setSearchParams({}, { replace: true });
                    }}
                >
                    Clear filters
                </p>
                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {!memberUuid ? (
                <p>Enter a memberUuid to query Athena snapshots.</p>
            ) : isLoading ? (
                <p>Loading Athena snapshots...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"service-member"}
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    noResultsMessage="No Athena snapshots found."
                    ref={ref}
                />
            )}

            <Modal isOpen={isCompareOpen} onClose={() => {
                setIsCompareOpen(false);
                setCompareBeforeRow(null);
                setCompareAfterRow(null);
                setCompareError(null);
            }}>
                <div style={{ padding: "1rem", width: "100%", height: "100%", overflowY: "scroll" }}>
                    <p className="title">Snapshot diff</p>
                    <p className="sub-title">
                        {orderedDiff?.leftTitle} → {orderedDiff?.rightTitle}
                    </p>
                    {compareLoading && <p style={{ padding: "1rem 0" }}>Loading full snapshot records from Athena...</p>}
                    {compareError && <p className="danger" style={{ padding: "1rem 0" }}>{compareError}</p>}
                    {!compareLoading && orderedDiff && (
                        <div style={{ paddingTop: "1rem" }}>
                            {changedFields.length ? (
                                <p style={{ padding: "0 0 1rem 0" }}>
                                    Changed fields: {changedFields.join(', ')}
                                </p>
                            ) : (
                                <p style={{ padding: "0 0 1rem 0" }}>
                                    These snapshots have the same member data. Daily copies often match when the DynamoDB record did not change.
                                </p>
                            )}
                            <ReactDiffViewer
                                oldValue={orderedDiff.oldValue}
                                newValue={orderedDiff.newValue}
                                leftTitle={orderedDiff.leftTitle}
                                rightTitle={orderedDiff.rightTitle}
                                disableWordDiff={true}
                                splitView={true}
                                showDiffOnly={changedFields.length > 0}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default ServiceMember;
